import axiosInstance from '../../../../config/axios';

export type EmailTemplateType =
  | 'OFFER'
  | 'ORDER_CONFIRMATION'
  | 'INVOICE'
  | 'REMINDER'
  | 'DUNNING_1'
  | 'DUNNING_2'
  | 'DUNNING_3';

export interface EmailTemplate {
  id: number;
  type: EmailTemplateType;
  language: string;
  subject_template: string;
  body_html_template: string;
  body_text_template?: string | null;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmailTemplateListItem {
  id: number | null;
  type: EmailTemplateType;
  language: string;
  subject_template: string;
  is_customized: boolean;
  is_active: boolean;
  updated_at: string | null;
}

export interface CreateEmailTemplatePayload {
  type: EmailTemplateType;
  language?: string;
  subject_template: string;
  body_html_template: string;
  body_text_template?: string | null;
  is_active?: boolean;
}

export interface UpdateEmailTemplatePayload {
  subject_template?: string;
  body_html_template?: string;
  body_text_template?: string | null;
  is_active?: boolean;
}

export interface EmailTemplatePreview {
  subject: string;
  body_html: string;
  body_text: string;
}

export interface PreviewContext {
  offer_id?: number;
  invoice_id?: number;
  partner_id?: number;
}

export const EMAIL_TEMPLATE_TYPE_OPTIONS: { value: EmailTemplateType; label: string; description: string }[] = [
  { value: 'OFFER', label: 'Angebot', description: 'E-Mail beim Versenden eines Angebots' },
  { value: 'ORDER_CONFIRMATION', label: 'Auftragsbestätigung', description: 'E-Mail beim Versenden einer Auftragsbestätigung' },
  { value: 'INVOICE', label: 'Rechnung', description: 'E-Mail beim Versenden einer Rechnung' },
  { value: 'REMINDER', label: 'Zahlungserinnerung', description: 'Freundliche Erinnerung vor Fälligkeit' },
  { value: 'DUNNING_1', label: 'Mahnung 1', description: 'Erste Mahnung nach Fälligkeit' },
  { value: 'DUNNING_2', label: 'Mahnung 2', description: 'Zweite Mahnung' },
  { value: 'DUNNING_3', label: 'Mahnung 3', description: 'Letzte Mahnung vor Inkasso' },
];

export const AVAILABLE_PLACEHOLDERS: { category: string; placeholders: { key: string; description: string }[] }[] = [
  {
    category: 'Unternehmen',
    placeholders: [
      { key: '{{ tenant.company_name }}', description: 'Firmenname' },
      { key: '{{ tenant.legal_name }}', description: 'Rechtlicher Name' },
      { key: '{{ tenant.street }}', description: 'Straße' },
      { key: '{{ tenant.postal_code }}', description: 'PLZ' },
      { key: '{{ tenant.city }}', description: 'Stadt' },
      { key: '{{ tenant.phone }}', description: 'Telefon' },
      { key: '{{ tenant.email }}', description: 'E-Mail' },
    ],
  },
  {
    category: 'Kunde',
    placeholders: [
      { key: '{{ partner.display_name }}', description: 'Anzeigename' },
      { key: '{{ partner.company_name }}', description: 'Firmenname' },
      { key: '{{ partner.salutation }}', description: 'Anrede (Sehr geehrte/r...)' },
      { key: '{{ partner.city }}', description: 'Stadt' },
    ],
  },
  {
    category: 'Angebot',
    placeholders: [
      { key: '{{ offer.offer_number }}', description: 'Angebotsnummer' },
      { key: '{{ offer.title }}', description: 'Titel' },
      { key: '{{ offer.total_net }}', description: 'Nettobetrag' },
      { key: '{{ offer.total_gross }}', description: 'Bruttobetrag' },
      { key: '{{ offer.valid_until }}', description: 'Gültig bis' },
    ],
  },
  {
    category: 'Rechnung',
    placeholders: [
      { key: '{{ invoice.invoice_number }}', description: 'Rechnungsnummer' },
      { key: '{{ invoice.title }}', description: 'Titel' },
      { key: '{{ invoice.total_net }}', description: 'Nettobetrag' },
      { key: '{{ invoice.total_gross }}', description: 'Bruttobetrag' },
      { key: '{{ invoice.due_date }}', description: 'Fälligkeitsdatum' },
      { key: '{{ invoice.days_overdue }}', description: 'Tage überfällig' },
    ],
  },
];

const BASE = '/api/admin/email-templates/';

export default class EmailTemplateService {
  static async list(params?: { type?: EmailTemplateType; language?: string }): Promise<EmailTemplateListItem[]> {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.set('type', params.type);
    if (params?.language) queryParams.set('language', params.language);

    const query = queryParams.toString();
    const url = query ? `${BASE}?${query}` : BASE;

    const response = await axiosInstance.get<EmailTemplateListItem[]>(url);
    return response.data;
  }

  static async get(id: number): Promise<EmailTemplate> {
    const response = await axiosInstance.get<EmailTemplate>(`${BASE}${id}/`);
    return response.data;
  }

  static async getByType(type: EmailTemplateType, language?: string): Promise<EmailTemplate> {
    const params = new URLSearchParams({ type });
    if (language) params.set('language', language);
    const response = await axiosInstance.get<EmailTemplate>(`${BASE}by-type/?${params.toString()}`);
    return response.data;
  }

  static async create(payload: CreateEmailTemplatePayload): Promise<EmailTemplate> {
    const response = await axiosInstance.post<EmailTemplate>(BASE, payload);
    return response.data;
  }

  static async update(id: number, payload: UpdateEmailTemplatePayload): Promise<EmailTemplate> {
    const response = await axiosInstance.patch<EmailTemplate>(`${BASE}${id}/`, payload);
    return response.data;
  }

  static async delete(id: number): Promise<void> {
    await axiosInstance.delete(`${BASE}${id}/`);
  }

  static async preview(type: EmailTemplateType, context: PreviewContext): Promise<EmailTemplatePreview> {
    const response = await axiosInstance.post<EmailTemplatePreview>(`${BASE}preview/`, {
      type,
      ...context,
    });
    return response.data;
  }

  static async resetToDefault(type: EmailTemplateType, language?: string): Promise<void> {
    await axiosInstance.post(`${BASE}reset/`, { type, language });
  }

  static getTypeLabel(type: EmailTemplateType): string {
    const option = EMAIL_TEMPLATE_TYPE_OPTIONS.find((opt) => opt.value === type);
    return option?.label || type;
  }

  static getTypeDescription(type: EmailTemplateType): string {
    const option = EMAIL_TEMPLATE_TYPE_OPTIONS.find((opt) => opt.value === type);
    return option?.description || '';
  }
}
